import { useCallback, useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import useToggledState from '@zenbusiness/application-commons-hooks/useToggledState';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Divider from '@zenbusiness/zui/Divider';

import { VeloCtaRow } from '#client/components/VeloCtaRow';
import {
  JOURNEYS_API_V4_SERVICES,
  JOURNEYS_V4_REDIRECTION_ANNUAL_REPORT,
  RELEASE_VELO_ORDER_STATUS,
} from '#client/constants/experimentsFlags';
import useBankingApplicationStatus from '#client/hooks/useBankingApplicationStatus';
import { useOrderTrackerStatus } from '#client/hooks/useOrderTrackerStatus';
import { useUrlResolver } from '#client/hooks/useUrlResolver';

import { INFO_TOOLTIP_MESSAGES, SERVICE_EVENT_PROPERTIES } from './constants';
import * as Styled from './OrderTracker.style';
import OrderTrackerItem from './OrderTrackerItem';
import OrderTrackerUpsells from './OrderTrackerUpsells';
import {
  useGetTimelineItems,
  getInfoTooltipDescriptionId,
  getTimelineItemsWithRedirectLinks,
  getTitleMessageSuffix,
  isAnnualReportV4Eligible,
  shouldFetchBankingStatus,
  shouldHideBankingTask,
} from './utils';

import { AiSlideIn } from '../AiSlideIn';
import { HomeSection } from '../Section';

function OrderTracker() {
  const { formatMessage } = useIntl();
  // businessEntityUuid is still required for whether the Velo CTA can render.
  const { businessEntityUuid, businessEntity } = useBusinessEntity();
  const formationStatus = businessEntity?.formationStatus;
  const { orderTasksWithStatus } = useOrderTrackerStatus();
  // An incomplete task links to its post-purchase completion route — the same
  // `checkout_page` route table the full-page checkout completion resolves against.
  const resolve = useUrlResolver();
  const isAnnualReportV4RedirectionEnabled = useExperimentFlag(JOURNEYS_V4_REDIRECTION_ANNUAL_REPORT, false);
  const v4ServicesByService = useExperimentFlag(JOURNEYS_API_V4_SERVICES, {});
  const getTaskCompletionUrl = useCallback(
    (serviceCode, { orderUuid, jurisdictionCode } = {}) => {
      const flags = {
        llc_annual_report: isAnnualReportV4Eligible(jurisdictionCode, {
          isRedirectionEnabled: isAnnualReportV4RedirectionEnabled,
          v4ServicesByService,
        }),
      };
      const { url, found } = resolve(serviceCode, { context: 'checkout_page', orderUuid, flags });
      return found ? url : null;
    },
    [resolve, isAnnualReportV4RedirectionEnabled, v4ServicesByService],
  );
  const isVeloOrderStatusEnabled = useExperimentFlag(RELEASE_VELO_ORDER_STATUS, false);
  const [selectedTask, setSelectedTask] = useState(null);
  const {
    state: orderTrackerDetailOpen,
    on: orderTrackerDetailOpenHandler,
    off: orderTrackerDetailCloseHandler,
  } = useToggledState(false);

  const allowedTimelineItems = useGetTimelineItems(orderTasksWithStatus);

  // Check if we have any pending banking tasks that need application status
  const hasPendingBankingTask = allowedTimelineItems.some(shouldFetchBankingStatus);
  const { applicationStatus: bankingApplicationStatus } = useBankingApplicationStatus(hasPendingBankingTask);

  // Filter out banking tasks with final statuses
  const visibleTimelineItems = allowedTimelineItems.filter(
    (item) => !shouldHideBankingTask(item, bankingApplicationStatus),
  );
  const shouldShowVeloCta = isVeloOrderStatusEnabled && businessEntityUuid;

  // Clickstream callback for opening AiSlideIn (called unconditionally per Rules of Hooks)
  const openSlideInEvent = useButtonPressedCallback({
    elementName: 'open_order_tracker_smart_assistant',
    pageSlug: 'order_tracker_smart_assistant',
  });

  const openExternalLinkEvent = useButtonPressedCallback({
    elementName: 'open_order_tracker_external_link',
    pageSlug: 'order_tracker',
  });

  const closeButtonOrderTrackerPressEvent = useButtonPressedCallback({
    elementName: 'close_order_tracker_smart_assistant',
    elementText: selectedTask?.type,
    elementValue: selectedTask?.type,
    pageSlug: 'order_tracker_smart_assistant',
  });

  const handleOrderTrackerDetailOpen = (item) => {
    setSelectedTask(item);
    orderTrackerDetailOpenHandler();
  };

  const getItemClickProps = (item) => {
    // link to journey
    if (item.linkTo) {
      return {
        isClickable: true,
        eventCallback: openExternalLinkEvent,
        onClick: () => {
          window.location.href = item.linkTo;
        },
      };
    }

    if (item.hasExternalLink && shouldShowVeloCta) {
      return { isClickable: false };
    }

    if (!shouldShowVeloCta) {
      return {
        isClickable: true,
        eventCallback: openSlideInEvent,
        onClick: () => handleOrderTrackerDetailOpen(item),
      };
    }

    return { isClickable: false };
  };

  const handleOrderTrackerDetailClose = () => {
    setSelectedTask(null);
    orderTrackerDetailCloseHandler();
    closeButtonOrderTrackerPressEvent();
  };

  if (!businessEntity || visibleTimelineItems.length === 0) {
    return null;
  }

  const allowedTimelineItemsObject = visibleTimelineItems.reduce((acc, { status, type, data }) => {
    acc[type] = { status };
    if (data?.estimatedFormationDate) {
      acc[type].estimatedFormationDate = data.estimatedFormationDate;
    }
    return acc;
  }, {});

  const timelineItems = isVeloOrderStatusEnabled
    ? getTimelineItemsWithRedirectLinks(visibleTimelineItems, getTaskCompletionUrl, { formationStatus })
    : visibleTimelineItems;
  const lastItemIndex = timelineItems.length - 1;

  return (
    <HomeSection title={<FormattedMessage id="dashboard.orderTracker.title" />}>
      <Styled.TrackerCard>
        {shouldShowVeloCta && <VeloCtaRow />}
        {timelineItems.map((item, index) => (
          <Styled.ListItemContainer key={item.type}>
            <OrderTrackerItem
              color={item.color}
              ctaMessageId={`dashboard.orderTracker.orderTaskStatus.${item.status}`}
              titleMessageId={`dashboard.orderTracker.orderTaskType.${item.type}`}
              titleMessageSuffix={getTitleMessageSuffix(item)}
              type={item.type}
              showChevron={false}
              hoverTooltipMessageId={item.hoverTooltipMessageId}
              infoTooltipTitleId={INFO_TOOLTIP_MESSAGES[item.type]?.titleId}
              infoTooltipMessageId={getInfoTooltipDescriptionId(item)}
              {...getItemClickProps(item)}
            />
            {
              /* if it's the first card show upsells */
              index === 0 && <OrderTrackerUpsells type={item.type} />
            }
            {index !== lastItemIndex && <Divider />}
          </Styled.ListItemContainer>
        ))}
      </Styled.TrackerCard>

      {selectedTask && !shouldShowVeloCta && (
        <AiSlideIn
          title={formatMessage({
            id: `dashboard.orderTracker.orderTaskType.${selectedTask.type}`,
          })}
          reasonsReplacements={{
            'order status overview': 'Overview',
            'order status what to expect': 'What to Expect',
          }}
          promptSlug="order-task-smart-assist"
          fillData={{
            product: selectedTask.type,
            data: JSON.stringify(allowedTimelineItemsObject),
          }}
          aiSlideInOpen={orderTrackerDetailOpen}
          closeHandler={handleOrderTrackerDetailClose}
          eventingProperties={{
            elementName: 'order_tracker_smart_assistant',
            elementValue: SERVICE_EVENT_PROPERTIES[selectedTask.type].elementValue,
            elementText: SERVICE_EVENT_PROPERTIES[selectedTask.type].elementText,
            aiProjectName: 'Order Tracker Detail',
          }}
          errorMessage={formatMessage({ id: 'dashboard.orderTracker.errorGeneric' })}
          footer={formatMessage({ id: 'dashboard.orderTracker.footer' })}
        />
      )}
    </HomeSection>
  );
}

export default OrderTracker;
